import {
  type BookableItem,
  type Entity,
  type EntityTier,
  type Ticket,
  type Design,
  type BookableItemTranslations,
  type Zone,
  type ParticipantGroup,
  type Invitation,
  type NotificationTemplate,
  type FormEntity,
  BookableItemConfigSource,
} from "client";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type BookableItemWithRelations = BookableItem & {
  designs?: Design[];
  translations?: BookableItemTranslations[];
  zones?: Zone[];
  participantGroups?: ParticipantGroup[];
  invitations?: Invitation[];
  notificationTemplates?: NotificationTemplate[];
  formEntities?: FormEntity[];
};

export type TicketWithBookableItem = Ticket & {
  bookableItem: BookableItemWithRelations;
};

export type EntityTierWithBookableItem = EntityTier & {
  bookableItem: BookableItemWithRelations;
};

export type EntityWithRelations = Entity & {
  bookableItem?: BookableItemWithRelations;
  entityTier: EntityTierWithBookableItem;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get effective BookableItem for an entity based on configSource
 * @param entity Entity with bookableItem and entityTier relations loaded
 * @returns The effective BookableItem to use
 */
export function getEffectiveBookableItem(
  entity: EntityWithRelations
): BookableItem {
  return entity.configSource === BookableItemConfigSource.ENTITY &&
    entity.bookableItem
    ? entity.bookableItem
    : entity.entityTier?.bookableItem;
}

/**
 * Get effective BookableItem ID for an entity
 * @param entity Entity with entityTier relation loaded
 * @returns The ID of the effective BookableItem
 */
export function getEffectiveBookableItemId(
  entity: EntityWithRelations
): string {
  return entity.configSource === BookableItemConfigSource.ENTITY &&
    entity.bookableItemId
    ? entity.bookableItemId
    : entity.entityTier.bookableItemId;
}

/**
 * Check if entity is using its own BookableItem config
 * @param entity Entity to check
 * @returns true if entity uses its own config, false if it uses tier config
 */
export function isEntityUsingOwnConfig(entity: Entity): boolean {
  return entity.configSource === BookableItemConfigSource.ENTITY;
}

/**
 * Check if entity is using tier config
 * @param entity Entity to check
 * @returns true if entity uses tier config, false if it uses own config
 */
export function isEntityUsingTierConfig(entity: Entity): boolean {
  return entity.configSource === BookableItemConfigSource.TIER;
}

// ============================================================================
// BACKWARD COMPATIBILITY HELPERS
// ============================================================================

/**
 * Flatten BookableItem fields to root level for backward compatibility
 * Maps BookableItem fields back to ticket object structure
 * IMPORTANT: Converts formEntities array to single formEntity for backward compatibility
 * @param ticket Ticket with bookableItem relation loaded
 * @returns Ticket with BookableItem fields at root level
 */
export function flattenTicketWithBookableItem(ticket: TicketWithBookableItem) {
  // Convert formEntities array to single formEntity (backward compatibility)
  const formEntity = ticket.bookableItem.formEntities?.[0] || null;

  const flattenedTicket = {
    ...ticket,
    // Map BookableItem fields to root level
    price: ticket.bookableItem.price,
    saleStartDate: ticket.bookableItem.saleStartDate,
    saleEndDate: ticket.bookableItem.saleEndDate,
    quantity: ticket.bookableItem.quantity,
    maxAmount: ticket.bookableItem.maxAmount,
    minAmount: ticket.bookableItem.minAmount,
    soldTogether: ticket.bookableItem.soldTogether,
    taxable: ticket.bookableItem.taxable,
    accessCode: ticket.bookableItem.accessCode,
    token: ticket.bookableItem.token,
    requireApproval: ticket.bookableItem.requireApproval,
    requireOTP: ticket.bookableItem.requireOTP,
    visibility: ticket.bookableItem.visibility,
    privacy: ticket.bookableItem.privacy,
    showRemainingQuantity: ticket.bookableItem.showRemainingQuantity,
    showBeforeSaleStart: ticket.bookableItem.showBeforeSaleStart,
    showAfterSaleEnd: ticket.bookableItem.showAfterSaleEnd,
    showWhenSoldOut: ticket.bookableItem.showWhenSoldOut,
    showDescription: ticket.bookableItem.showDescription,
    showPriceToggle: ticket.bookableItem.showPriceToggle,
    showSaleEndsOnToggle: ticket.bookableItem.showSaleEndsOnToggle,
    externalLink: ticket.bookableItem.externalLink,
    // Relations
    designs: ticket.bookableItem.designs,
    translations: ticket.bookableItem.translations,
    zones: ticket.bookableItem.zones,
    participantGroups: ticket.bookableItem.participantGroups,
    invitations: ticket.bookableItem.invitations,
    notificationTemplates: ticket.bookableItem.notificationTemplates,
    // Backward compatibility: singular formEntity (first item from array)
    formEntity: formEntity,
    // Also include the array for new code that needs it
    formEntities: ticket.bookableItem.formEntities,
  };

  // Convert designs to ticketTemplates (old structure: { ticket, design, ticketId, designId })
  const ticketTemplates =
    ticket.bookableItem.designs?.map((design) => ({
      ticket: flattenedTicket,
      design,
      ticketId: ticket.id,
      designId: design.id,
    })) || [];

  return {
    ...ticket,
    ...flattenedTicket,
    id: ticket.id,

    // Backward compatibility: ticketTemplates from designs
    ticketTemplates,
  };
}

/**
 * Flatten ticket data from BookableItem (for cases where we have BookableItem with ticket relation)
 * @param bookableItem - BookableItem with ticket relation
 * @returns Flattened ticket object with BookableItem fields at root level
 */
export function flattenTicketFromBookableItem(bookableItem: {
  ticket: any;
  formEntities?: any[];
  [key: string]: any;
}) {
  if (!bookableItem.ticket) return null;

  // Convert formEntities array to single formEntity (backward compatibility)
  const formEntity = bookableItem.formEntities?.[0] || null;

  return {
    ...bookableItem.ticket,
    // Map BookableItem fields to root level
    price: bookableItem.price,
    saleStartDate: bookableItem.saleStartDate,
    saleEndDate: bookableItem.saleEndDate,
    quantity: bookableItem.quantity,
    maxAmount: bookableItem.maxAmount,
    minAmount: bookableItem.minAmount,
    soldTogether: bookableItem.soldTogether,
    taxable: bookableItem.taxable,
    accessCode: bookableItem.accessCode,
    token: bookableItem.token,
    requireApproval: bookableItem.requireApproval,
    requireOTP: bookableItem.requireOTP,
    visibility: bookableItem.visibility,
    privacy: bookableItem.privacy,
    showRemainingQuantity: bookableItem.showRemainingQuantity,
    showBeforeSaleStart: bookableItem.showBeforeSaleStart,
    showAfterSaleEnd: bookableItem.showAfterSaleEnd,
    showWhenSoldOut: bookableItem.showWhenSoldOut,
    showDescription: bookableItem.showDescription,
    showPriceToggle: bookableItem.showPriceToggle,
    showSaleEndsOnToggle: bookableItem.showSaleEndsOnToggle,
    externalLink: bookableItem.externalLink,
    // Relations
    designs: bookableItem.designs,
    translations: bookableItem.translations,
    zones: bookableItem.zones,
    participantGroups: bookableItem.participantGroups,
    invitations: bookableItem.invitations,
    notificationTemplates: bookableItem.notificationTemplates,
    // Backward compatibility: singular formEntity (first item from array)
    formEntity: formEntity,
    // Also include the array for new code that needs it
    formEntities: bookableItem.formEntities,
  };
}

// ============================================================================
// DATA EXTRACTION
// ============================================================================

/**
 * Separate shared fields (BookableItem) from ticket-specific fields
 * @param data Combined ticket data
 * @returns Object with { bookableItemData, ticketData }
 */
export function separateTicketFields(data: any): {
  bookableItemData: any;
  ticketData: any;
} {
  const {
    // Shared fields (go to BookableItem)
    price,
    saleStartDate,
    saleEndDate,
    quantity,
    maxAmount,
    minAmount,
    soldTogether,
    taxable,
    accessCode,
    token,
    requireApproval,
    requireOTP,
    visibility,
    privacy,
    showRemainingQuantity,
    showBeforeSaleStart,
    showAfterSaleEnd,
    showWhenSoldOut,
    showDescription,
    showPriceToggle,
    showSaleEndsOnToggle,
    externalLink,
    participantGroupIds,
    zoneIds,
    designIds,
    notificationTemplateIds,
    invitationIds,
    color,
    // Ticket-specific fields
    ...ticketSpecific
  } = data;

  return {
    bookableItemData: {
      price,
      color,
      saleStartDate,
      saleEndDate,
      quantity,
      maxAmount,
      minAmount,
      soldTogether,
      taxable,
      accessCode,
      token,
      requireApproval,
      requireOTP,
      visibility,
      privacy,
      showRemainingQuantity,
      showBeforeSaleStart,
      showAfterSaleEnd,
      showWhenSoldOut,
      showDescription,
      showPriceToggle,
      showSaleEndsOnToggle,
      externalLink,
      participantGroupIds,
      zoneIds,
      designIds,
      notificationTemplateIds,
      invitationIds,
    },
    ticketData: ticketSpecific,
  };
}
